import type React from "react";
import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Chip,
  Slider,
} from "@mui/material";
import { Close, CloudUpload } from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import domtoimage from "dom-to-image";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import TemplateRenderer from "./thumbnail-templates/TemplatesRenderer";
import type {
  IThumbnailAssignment,
  IThumbnailPreviewDialogProps,
} from "../types/thumbnail";
import type { IThumbnailTemplate } from "../../../../types/thumbnail-template";
import apiService from "../../../../service/Api";
import { RootState } from "../../../../store/Index";

const ThumbnailPreviewDialog: React.FC<IThumbnailPreviewDialogProps> = ({
  open,
  onClose,
  streamData,
  onSave,
  onDownload,
  refreshThumbnails,
  thumbnailType,
  refreshStream,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const [selectedTemplate, setSelectedTemplate] =
    useState<IThumbnailTemplate | null>(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showAllTemplates, setShowAllTemplates] = useState(false);
  const [customBackground, setCustomBackground] = useState<string>("");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const thumbnailRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bookingType = streamData?.bookingData?.booking_type;
  const deceasedId = streamData?.bookingData?.deceased_ids || [];
  const streamId = streamData?.id;

  const isConference = bookingType === "CONFERENCE";

  const [backgroundLoaded, setBackgroundLoaded] = useState(false);

  const categoryMap: Record<string, IThumbnailTemplate["category"]> = {
    FUNERAL: "funeral",
    CHURCH: "church",
    FUNERAL_HOME: "funeral_home",
    CONFERENCE: "conference",
  };

  const [templates, setTemplates] = useState<IThumbnailTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState<boolean>(true);

  const waitForFonts = async () => {
    if (document.fonts) {
      await document.fonts.ready;
    }
  };

  const fetchTemplates = async () => {
    try {
      const thumbnailTypeParam = thumbnailType
        ? `?thumbnail_type=${thumbnailType}`
        : "";
      const response = await apiService.get<{
        results: IThumbnailTemplate[];
      }>(`/api/configurations/thumbnails/${thumbnailTypeParam}`);
      setTemplates(response.data.results || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchTemplates();
    }
  }, [open]);

  useEffect(() => {
    setSelectedTemplate(null);
    setShowAllTemplates(false);
    setPreviewDialogOpen(true);
    setCustomBackground("");
    setUploadedFile(null);
  }, [open, thumbnailType]);

  const handleBackgroundUpload = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please upload an image file");
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setCustomBackground(result);
        setUploadedFile(file);
        toast.success("Background image uploaded");
      };
      reader.readAsDataURL(file);
    }
  };
  const handleDownload = async () => {
    if (!thumbnailRef.current || !selectedTemplate) return;

    if (!backgroundLoaded && isConference && customBackground) {
      toast.info("Please wait for the background image to load...");
      return;
    }

    const { width, height } =
      selectedTemplate.thumbnail_type === "aone"
        ? { width: 1600, height: 1129 }
        : { width: 1280, height: 720 };

    const dataUrl = await domtoimage.toPng(thumbnailRef.current, {
      width,
      height,
    });

    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = `thumbnail-${
      streamData?.bookingData?.deceased_name || streamData?.title || "event"
    }.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    if (onDownload) onDownload();
  };

  const handleSave = async () => {
    if (!selectedTemplate || !streamId) return;

    try {
      setIsSaving(true);

      const payload: any = {
        deceased_ids: deceasedId,
        stream_id: Number(streamId),
        thumbnail_id: selectedTemplate.id,
      };

      const { data } = await apiService.get<{
        results: IThumbnailAssignment[];
      }>(`/api/configurations/deceased-thumbnails/?stream=${streamId}`);

      const existing = data.results[0];
      if (existing) {
        await apiService.delete(
          `/api/configurations/deceased-thumbnails/${existing.id}/`
        );
      }

      await apiService.post(
        "/api/configurations/deceased-thumbnails/",
        payload
      );

      // 2️⃣ Upload conference background as actual file if provided
      if (isConference && uploadedFile) {
        const formData = new FormData();
        formData.append("stream_thumbnail_background_image", uploadedFile);

        try {
          await apiService.patch(`/api/streams/stream/${streamId}/`, formData);
          toast.success("Custom background saved to stream");
        } catch (patchError) {
          console.error("Stream background patch failed:", patchError);
          toast.error("Failed to upload stream background");
        }
      }

      toast.success("Thumbnail saved successfully");

      if (onSave) onSave("thumbnail-saved");
      if (refreshThumbnails) refreshThumbnails();
      if (refreshStream) await refreshStream();
      onClose();
    } catch (error: any) {
      console.error("Thumbnail save error", error);
      toast.error("Failed to save thumbnail");
    } finally {
      setIsSaving(false);
    }
  };

  const handleChooseDifferent = () => {
    setShowAllTemplates(true);
    setPreviewDialogOpen(false);
  };

  const getCategoryColor = (category: IThumbnailTemplate["category"]) => {
    switch (category) {
      case "funeral":
        return "primary";
      case "church":
        return "secondary";
      case "funeral_home":
        return "success";
      case "conference":
        return "info";
      default:
        return "default";
    }
  };

  const displayTemplates = templates.filter((template) => {
    const isActive = template.is_active;
    const matchesThumbnailType =
      !thumbnailType || template.thumbnail_type === thumbnailType;
    const matchesBookingType =
      !bookingType ||
      template.category ===
        categoryMap[bookingType as keyof typeof categoryMap];

    return isActive && matchesThumbnailType && matchesBookingType;
  });

  const renderThumbnailComponent = () => {
    if (!selectedTemplate) return null;

    const props = {
      ref: thumbnailRef,
      streamData,
      template: selectedTemplate,
      customBackground: isConference ? customBackground : undefined,
      style: {
        width: selectedTemplate.thumbnail_type === "aone" ? "1600px" : "1280px",
        height: selectedTemplate.thumbnail_type === "aone" ? "1129px" : "720px",
      },
    };

    return <TemplateRenderer {...props} />;
  };
  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept="image/*"
        onChange={handleBackgroundUpload}
      />

      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="h5">
            {showAllTemplates ? "Choose a Template" : "Thumbnail Preview"}
          </Typography>
          <Close onClick={onClose} style={{ cursor: "pointer" }} />
        </DialogTitle>

        <DialogContent sx={{ minHeight: "500px", py: 3 }}>
          {selectedTemplate && !showAllTemplates ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={2}
            >
              <Box sx={{ transform: "scale(0.6)", transformOrigin: "center" }}>
                {renderThumbnailComponent()}
              </Box>
            </Box>
          ) : (
            <Grid container spacing={2}>
              <AnimatePresence>
                {displayTemplates.map((template, index) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={template.id}>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                    >
                      <Card
                        onClick={() => {
                          setSelectedTemplate(template);
                          setPreviewDialogOpen(true);
                          setShowAllTemplates(false);
                        }}
                        sx={{
                          cursor: "pointer",
                          border: "1px solid #ccc",
                          "&:hover": { boxShadow: 6 },
                        }}
                      >
                        <CardMedia
                          component="img"
                          height="150"
                          image={
                            template.foreground_image ||
                            template.background_image
                          }
                          alt={template.name}
                        />
                        <CardContent>
                          <Typography variant="h6">{template.name}</Typography>
                          <Chip
                            label={template.category.replace("_", " ")}
                            color={getCategoryColor(template.category)}
                          />
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Grid>
                ))}
              </AnimatePresence>
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "space-between", p: 2 }}>
          <Box>
            {!showAllTemplates && selectedTemplate && !isConference && (
              <Button onClick={handleChooseDifferent}>
                Choose Different Template
              </Button>
            )}
          </Box>

          <Box display="flex" gap={2}>
            {selectedTemplate && !showAllTemplates && (
              <>
                {isConference && (
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<CloudUpload />}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {customBackground
                        ? "Change Background Image"
                        : "Upload Background Image"}
                    </Button>
                    {/* {customBackground && (
                      <Button
                        variant="text"
                        color="error"
                        onClick={() => {
                          setCustomBackground("");
                          setUploadedFile(null);
                        }}
                        sx={{ ml: 2 }}
                      >
                        Remove
                      </Button>
                    )} */}
                  </Box>
                )}
                <Button onClick={handleDownload}>Download</Button>
                <Button
                  onClick={handleSave}
                  variant="contained"
                  disabled={isSaving}
                >
                  {isSaving ? <CircularProgress size={20} /> : "Save"}
                </Button>
              </>
            )}
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ThumbnailPreviewDialog;
